import * as Sentry from '@sentry/react';
import { TRPCClientError } from '@trpc/client';
import { useNavigate } from 'react-router-dom';
import { useSystemStore } from '../stores/SystemStore';
import { useUserStore } from '../stores/UserStore';
import { trcpProxyClient } from '../utils/trcp';
import { useGroupSettings } from './useGroupSettings';
import { useSetUserToken } from './useSetUserToken';

type Fn = (cb?: () => void) => void;

export const useRefetchUser = (): Fn => {
	const { refetch: groupSettingsRefetch } = useGroupSettings();
	const setSchools = useUserStore(state => state.setSchools);
	const setGroupUserSettings = useUserStore(state => state.setGroupUserSettings);
	const setNotifications = useUserStore(state => state.setNotifications);
	const setFeatureFlags = useUserStore(state => state.setFeatureFlags);
	const setIsGroupBlocked = useUserStore(state => state.setIsGroupBlocked);
	const navigate = useNavigate();
	const setUser = useSystemStore(state => state.setUser);
	const setToken = useSetUserToken();
	const setLogfinFlowVisible = useSystemStore(state => state.setLogfinFlowVisible);
	const setBooting = useSystemStore(state => state.setBooting);
	const refetError = () => {
		setToken();
		setBooting(false);
		navigate('/');
	};

	const refetchUserFeatureFlags = async () => {
		const userFeatureFlags = await trcpProxyClient.user.getUserFeatureFlags.query();
		setFeatureFlags(userFeatureFlags);
	};

	const refetchNotifications = async () => {
		const notifications = await trcpProxyClient.user.getNotifications.query();
		setNotifications(notifications);
	};

	const refetchSchools = async () => {
		const schools = await trcpProxyClient.user.getUserSchools.query();
		setSchools(schools);
	};

	const refetchGroupUserSettings = async () => {
		const groupUserSettings = await trcpProxyClient.user.getGroupUserSettings.query();
		setGroupUserSettings(groupUserSettings);
	};

	const refetchIsGroupBlocked = async (schoolId: string) => {
		const isGroupBlocked = await trcpProxyClient.user.getIsGroupBlocked.query({ schoolId });
		setIsGroupBlocked(isGroupBlocked);
	};

	const refetch = (cb?: () => void, credits = 3): void => {
		trcpProxyClient.user.meV2
			.query()
			.then(async data => {
				setLogfinFlowVisible(!data.isAccountInitialized);

				setUser(data);
				Sentry.setUser({
					id: data.id,
				});

				refetchUserFeatureFlags();
				refetchNotifications();
				refetchSchools();
				refetchGroupUserSettings();
				refetchIsGroupBlocked(data.activeSchoolId);
				groupSettingsRefetch();

				await new Promise(r => setTimeout(r, 100));
				cb?.();
			})
			.catch(e => {
				// eslint-disable-next-line no-console
				console.log('Error fetching user');

				if (e instanceof TRPCClientError) {
					if (e.data?.httpStatus === 401) {
						refetError();
						return;
					}
				}

				if (credits > 0) {
					setTimeout(() => {
						refetch(cb, credits - 1);
					}, 1000);
				} else {
					refetError();
				}
			});
	};

	return refetch;
};
